import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import PolicyRoundedIcon from '@mui/icons-material/PolicyRounded'
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { useEffect, useState, type ReactNode } from 'react'
import { useToast } from '../../../context/useToast'
import type { Workspace, WorkspaceRole } from '../../../context/WorkspaceContext'
import { roleCanPerform } from '../../../utils/workspacePermissions'
import { useNls } from '../../../nls/nlsProvider'
import type { AnalysisIncludedSystemKey, AnalysisRowResolution, AnalysisRowResolutionScope, AnalysisSnapshot, AnalysisTimeRangeKey } from '../../../types/analysis'
import { Button } from '../../Common/Button'
import { RailPageShellCard } from '../../Common/RailPageShell'
import { MetricCard } from '../MetricCard'

import { createAnalysisAlertChecklist } from '../../../services/analysisAlertChecklistService'
import { buildAnchor, reopenRow, resolveRow, subscribeResolutions } from '../../../services/analysisRowResolutionService'
import { firebaseAuth } from '../../../services/firebase'
import {
  isSparklineAnomaly,
  LiveModuleCard,
  useAuditEvents,
  useAuditSignalsModule,
  useExportActivityModule,
  useProposalExecutionModule,
  useRetentionStateModule,
  useSecurityCuesModule,
  useSensitiveOpsModule,
} from '../modules'
import { buildLiveModuleDescriptor, buildMetricDescriptor } from '../popover/descriptors'

import { formatNumber } from './analysisTabShared'
import type { AnalysisNavigate } from './analysisTabShared'

export const GovernanceTab = ({
  workspace,
  role,
  timeRange,
  snapshot,
  snapshotNonce,
  onNavigate,
  onOpenReports,
  includedSystems,
  highlightAnomalies,
}: {
  workspace: Workspace
  role: WorkspaceRole
  timeRange: AnalysisTimeRangeKey
  snapshot: AnalysisSnapshot
  snapshotNonce: number
  onNavigate: AnalysisNavigate
  onOpenReports: () => void
  includedSystems?: Set<AnalysisIncludedSystemKey>
  highlightAnomalies?: boolean
}) => {
  const { t } = useNls()
  const { showToast } = useToast()
  const [resolutions, setResolutions] = useState<Map<string, AnalysisRowResolution>>(new Map())
  const govMetrics: Array<{
    title: string
    value: string
    detail: string
    icon: ReactNode
    tone?: 'default' | 'warn' | 'good'
    numeric: number
  }> = [
    {
      title: 'Recent activity',
      value: formatNumber(snapshot.governance.recentlyUpdated),
      detail: 'Local update markers in the last day',
      icon: <PolicyRoundedIcon />,
      numeric: snapshot.governance.recentlyUpdated,
    },
    {
      title: 'Sensitive pressure',
      value: formatNumber(snapshot.work.blocked + snapshot.governance.highPriority),
      detail: 'Blocked plus high-priority records',
      icon: <WarningAmberRoundedIcon />,
      tone: snapshot.work.blocked ? ('warn' as const) : ('good' as const),
      numeric: snapshot.work.blocked + snapshot.governance.highPriority,
    },
  ]
  // Shared audit query for Governance modules
  const {
    records: auditRecords,
    loading: auditLoading,
    error: auditError,
  } = useAuditEvents({
    workspaceId: workspace.id,
    timeRange,
    refreshKey: snapshotNonce,
  })
  useEffect(
    () =>
      subscribeResolutions({
        workspaceId: workspace.id,
        onChange: setResolutions,
      }),
    [workspace.id],
  )
  const resolutionActions = (scope: AnalysisRowResolutionScope) => (record: { id: string; primary: string }) => {
    const actorId = firebaseAuth.currentUser?.uid
    const currentResolution = resolutions.get(buildAnchor(scope, record.id))
    const save = (state: 'ACK' | 'DISMISSED') => {
      if (!actorId) return
      void resolveRow({
        workspaceId: workspace.id,
        scope,
        recordId: record.id,
        actorId,
        state,
      })
        .then(() => showToast(t(state === 'ACK' ? 'NLSAnalysisRowResolutionAckSucceeded' : 'NLSAnalysisRowResolutionDismissSucceeded'), 'success'))
        .catch(() => showToast(t(state === 'ACK' ? 'NLSAnalysisRowResolutionAckFailed' : 'NLSAnalysisRowResolutionDismissFailed'), 'error'))
    }
    return {
      currentResolution,
      canEdit: role !== 'VIEWER',
      onAcknowledge: () => save('ACK'),
      onDismiss: () => save('DISMISSED'),
      onConvertToTask: () => {
        if (!actorId) return
        void createAnalysisAlertChecklist({
          workspaceId: workspace.id,
          title: t('NLSAnalysisRowResolutionTaskItemTitle', {
            scope,
            recordPrimary: record.primary,
          }),
          assigneeUid: actorId,
          sourceRowAnchor: buildAnchor(scope, record.id),
        })
          .then((resultTaskItemId) =>
            resolveRow({
              workspaceId: workspace.id,
              scope,
              recordId: record.id,
              actorId,
              state: 'CONVERTED',
              resultTaskItemId,
            }),
          )
          .then(() => showToast(t('NLSAnalysisRowResolutionConvertSucceeded'), 'success'))
          .catch(() => showToast(t('NLSAnalysisRowResolutionConvertFailed'), 'error'))
      },
      onReopen: () => {
        if (!currentResolution) return
        void reopenRow({
          workspaceId: workspace.id,
          anchor: currentResolution.anchor,
        })
          .then(() => showToast(t('NLSAnalysisRowResolutionReopenSucceeded'), 'success'))
          .catch(() => showToast(t('NLSAnalysisRowResolutionReopenFailed'), 'error'))
      },
    }
  }

  const auditSignals = useAuditSignalsModule({
    workspaceId: workspace.id,
    timeRange,
    auditRecords,
    loading: auditLoading,
    error: auditError,
  })

  const sensitiveOps = useSensitiveOpsModule({
    workspaceId: workspace.id,
    timeRange,
    auditRecords,
    loading: auditLoading,
    error: auditError,
    resolutions,
  })

  const retentionState = useRetentionStateModule({
    workspace,
    timeRange,
    auditRecords,
    loading: auditLoading,
    error: auditError,
    resolutions,
  })

  const exportActivity = useExportActivityModule({
    workspaceId: workspace.id,
    timeRange,
    auditRecords,
    loading: auditLoading,
    error: auditError,
    refreshKey: snapshotNonce,
  })

  const proposalExecution = useProposalExecutionModule({
    workspaceId: workspace.id,
    uid: firebaseAuth.currentUser?.uid,
    canApprove: roleCanPerform(role, 'EDITOR'),
    timeRange,
    refreshKey: snapshotNonce,
  })

  const securityCues = useSecurityCuesModule({
    workspaceId: workspace.id,
    timeRange,
    auditRecords,
    loading: auditLoading,
    error: auditError,
    resolutions,
  })

  if (includedSystems && !includedSystems.has('GOVERNANCE')) {
    return (
      <div className="analysisFilteredStateEmpty" data-testid="governance-filtered-empty">
        <strong>{t('NLSAnalysisFilteredEmptyHeading')}</strong>
        <p>{t('NLSAnalysisFilteredEmptyDesc')}</p>
      </div>
    )
  }

  return (
    <div className="analysisStageStack">
      <div className="analysisMetricGrid">
        {govMetrics.map((metric) => (
          <MetricCard
            key={metric.title}
            title={metric.title}
            value={metric.value}
            detail={metric.detail}
            icon={metric.icon}
            tone={metric.tone}
            descriptor={buildMetricDescriptor({
              snapshot,
              tab: 'GOVERNANCE',
              title: metric.title,
              value: metric.numeric,
              detail: metric.detail,
              drillTarget: 'SUMMARY',
              onNavigate,
              onOpenReports,
            })}
          />
        ))}
      </div>
      <div className="analysisPhaseGrid">
        <>
          <LiveModuleCard
            icon={<PolicyRoundedIcon />}
            title={t('NLSAnalysisModuleAuditSignalsTitle')}
            eyebrow={t('NLSAnalysisLiveModuleEyebrowGovernance')}
            headline={auditSignals.headline}
            sparkline={auditSignals.sparkline}
            records={auditSignals.records}
            loading={auditSignals.loading}
            highlighted={highlightAnomalies && isSparklineAnomaly(auditSignals.sparkline.points)}
            onRecordClick={() => onNavigate('SUMMARY')}
            descriptor={buildLiveModuleDescriptor({
              snapshot,
              id: `audit-signals-${workspace.id}`,
              title: 'Audit Signals',
              tab: 'GOVERNANCE',
              cardLabel: 'Audit Signals',
              points: auditSignals.sparkline.points,
              records: auditSignals.records.map((r) => ({
                id: r.id,
                primary: r.primary,
                secondary: r.secondary,
                numeric: r.numeric,
                drillTarget: 'SUMMARY',
              })),
              direction: auditSignals.sparkline.direction,
              onNavigate,
              onOpenReports,
            })}
          />
          <LiveModuleCard
            icon={<WarningAmberRoundedIcon />}
            title={t('NLSAnalysisModuleSensitiveOpsTitle')}
            eyebrow={t('NLSAnalysisLiveModuleEyebrowGovernance')}
            headline={sensitiveOps.headline}
            sparkline={sensitiveOps.sparkline}
            records={sensitiveOps.records}
            loading={sensitiveOps.loading}
            highlighted={highlightAnomalies && isSparklineAnomaly(sensitiveOps.sparkline.points)}
            onRecordClick={() => onNavigate('SUMMARY')}
            resolutionActions={resolutionActions('sensitive-ops')}
            descriptor={buildLiveModuleDescriptor({
              snapshot,
              id: `sensitive-ops-${workspace.id}`,
              title: 'Sensitive Ops',
              tab: 'GOVERNANCE',
              cardLabel: 'Sensitive Ops',
              points: sensitiveOps.sparkline.points,
              records: sensitiveOps.records.map((r) => ({
                id: r.id,
                primary: r.primary,
                secondary: r.secondary,
                numeric: r.numeric,
                drillTarget: 'SUMMARY',
              })),
              direction: sensitiveOps.sparkline.direction,
              onNavigate,
              onOpenReports,
            })}
          />
          <LiveModuleCard
            icon={<HistoryRoundedIcon />}
            title={t('NLSAnalysisModuleRetentionStateTitle')}
            eyebrow={t('NLSAnalysisLiveModuleEyebrowGovernance')}
            headline={retentionState.headline}
            sparkline={retentionState.sparkline}
            records={retentionState.records}
            loading={retentionState.loading}
            highlighted={highlightAnomalies && isSparklineAnomaly(retentionState.sparkline.points)}
            onRecordClick={() => onNavigate('SUMMARY')}
            resolutionActions={resolutionActions('retention-state')}
            descriptor={buildLiveModuleDescriptor({
              snapshot,
              id: `retention-state-${workspace.id}`,
              title: 'Retention State',
              tab: 'GOVERNANCE',
              cardLabel: 'Retention State',
              points: retentionState.sparkline.points,
              records: retentionState.records.map((r) => ({
                id: r.id,
                primary: r.primary,
                secondary: r.secondary,
                numeric: r.numeric,
                drillTarget: 'SUMMARY',
              })),
              direction: retentionState.sparkline.direction,
              onNavigate,
              onOpenReports,
            })}
          />
          <LiveModuleCard
            icon={<IosShareRoundedIcon />}
            title={t('NLSAnalysisModuleExportActivityTitle')}
            eyebrow={t('NLSAnalysisLiveModuleEyebrowGovernance')}
            headline={exportActivity.headline}
            sparkline={exportActivity.sparkline}
            records={exportActivity.records}
            loading={exportActivity.loading}
            highlighted={highlightAnomalies && isSparklineAnomaly(exportActivity.sparkline.points)}
            onRecordClick={() => onNavigate('SUMMARY')}
            descriptor={buildLiveModuleDescriptor({
              snapshot,
              id: `export-activity-${workspace.id}`,
              title: 'Export Activity',
              tab: 'GOVERNANCE',
              cardLabel: 'Export Activity',
              points: exportActivity.sparkline.points,
              records: exportActivity.records.map((r) => ({
                id: r.id,
                primary: r.primary,
                secondary: r.secondary,
                numeric: r.numeric,
                drillTarget: 'SUMMARY',
              })),
              direction: exportActivity.sparkline.direction,
              onNavigate,
              onOpenReports,
            })}
          />
          <LiveModuleCard
            icon={<AutoAwesomeRoundedIcon />}
            title={t('NLSAnalysisModuleProposalExecutionTitle')}
            eyebrow={t('NLSAnalysisLiveModuleEyebrowGovernance')}
            headline={proposalExecution.headline}
            sparkline={proposalExecution.sparkline}
            records={proposalExecution.records}
            loading={proposalExecution.loading}
            highlighted={highlightAnomalies && isSparklineAnomaly(proposalExecution.sparkline.points)}
            onRecordClick={() => onNavigate('SUMMARY')}
            descriptor={buildLiveModuleDescriptor({
              snapshot,
              id: `proposal-execution-${workspace.id}`,
              title: 'Proposal Execution',
              tab: 'GOVERNANCE',
              cardLabel: 'Proposal Execution',
              points: proposalExecution.sparkline.points,
              records: proposalExecution.records.map((r) => ({
                id: r.id,
                primary: r.primary,
                secondary: r.secondary,
                numeric: r.numeric,
                drillTarget: 'SUMMARY',
              })),
              direction: proposalExecution.sparkline.direction,
              onNavigate,
              onOpenReports,
            })}
          />
          <LiveModuleCard
            icon={<SecurityRoundedIcon />}
            title={t('NLSAnalysisModuleSecurityCuesTitle')}
            eyebrow={t('NLSAnalysisLiveModuleEyebrowGovernance')}
            headline={securityCues.headline}
            sparkline={securityCues.sparkline}
            records={securityCues.records}
            loading={securityCues.loading}
            highlighted={highlightAnomalies && isSparklineAnomaly(securityCues.sparkline.points)}
            onRecordClick={() => onNavigate('SUMMARY')}
            resolutionActions={resolutionActions('security-cues')}
            descriptor={buildLiveModuleDescriptor({
              snapshot,
              id: `security-cues-${workspace.id}`,
              title: 'Security Cues',
              tab: 'GOVERNANCE',
              cardLabel: 'Security Cues',
              points: securityCues.sparkline.points,
              records: securityCues.records.map((r) => ({
                id: r.id,
                primary: r.primary,
                secondary: r.secondary,
                numeric: r.numeric,
                drillTarget: 'SUMMARY',
              })),
              direction: securityCues.sparkline.direction,
              onNavigate,
              onOpenReports,
            })}
          />
        </>
      </div>
      <RailPageShellCard className="analysisSetupCard analysisSetupCard--compact analysisSetupCard--source" heading="Ledger source" eyebrow="Governance">
        <p>Open the Ledger for authoritative audit rows and full retention detail.</p>
        <Button variant="secondary" size="sm" icon={<OpenInNewRoundedIcon />} onClick={() => onNavigate('SUMMARY')}>
          Open Ledger
        </Button>
      </RailPageShellCard>
    </div>
  )
}
